import React, { useState, useEffect } from 'react';
import { toDateInputValue } from '../utils/dateInput';
import { X } from 'react-feather';
import { tournamentAPI, Tournament } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useResponsive } from '../hooks/useResponsive';
import Modal from './Modal';
import { Select, TextInput, TextArea } from './ui';

interface AddEditTournamentModalProps {
  athleteId: string;
  tournament: Tournament | null;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}

const AddEditTournamentModal: React.FC<AddEditTournamentModalProps> = ({
  athleteId,
  tournament,
  onClose,
  onSuccess,
}) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    facility_name: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    start_date: '',
    end_date: '',
    notes: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (tournament) {
      setFormData({
        name: tournament.name || '',
        facility_name: tournament.facility_name || '',
        address1: tournament.address1 || '',
        address2: tournament.address2 || '',
        city: tournament.city || '',
        state: tournament.state || '',
        zip: tournament.zip || '',
        start_date: toDateInputValue(tournament.start_date),
        end_date: toDateInputValue(tournament.end_date),
        notes: tournament.notes || '',
      });
    }
  }, [tournament]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Tournament name is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state) {
      newErrors.state = 'State is required';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }

    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      if (end < start) {
        newErrors.end_date = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    if (!sessionToken) {
      toast.error('You must be logged in');
      return;
    }

    setSubmitting(true);

    try {
      const tournamentData = {
        athlete_profile_id: athleteId,
        name: formData.name.trim(),
        facility_name: formData.facility_name.trim() || undefined,
        address1: formData.address1.trim() || undefined,
        address2: formData.address2.trim() || undefined,
        city: formData.city.trim(),
        state: formData.state,
        zip: formData.zip.trim() || undefined,
        start_date: formData.start_date,
        end_date: formData.end_date,
        notes: formData.notes.trim() || undefined,
      };

      let response;
      if (tournament) {
        response = await tournamentAPI.updateTournament(
          sessionToken,
          tournament.id,
          tournamentData
        );
      } else {
        response = await tournamentAPI.createTournament(sessionToken, tournamentData);
      }

      if (response.success) {
        toast.success(
          tournament ? 'Tournament updated successfully' : 'Tournament created successfully'
        );
        await onSuccess();
        onClose();
      } else {
        // Don't silently no-op on success:false (audit #196).
        toast.error(`Failed to ${tournament ? 'update' : 'create'} tournament`);
      }
    } catch (error: any) {
      console.error('Submit error:', error);
      toast.error(
        error.message || `Failed to ${tournament ? 'update' : 'create'} tournament`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const styles = getStyles(isMobile);

  // Dirty tracking for the shared Modal's discard guard (audit #223).
  const isDirty = tournament
    ? (
        formData.name !== (tournament.name || '') ||
        formData.facility_name !== (tournament.facility_name || '') ||
        formData.address1 !== (tournament.address1 || '') ||
        formData.address2 !== (tournament.address2 || '') ||
        formData.city !== (tournament.city || '') ||
        formData.state !== (tournament.state || '') ||
        formData.zip !== (tournament.zip || '') ||
        formData.start_date !== toDateInputValue(tournament.start_date) ||
        formData.end_date !== toDateInputValue(tournament.end_date) ||
        formData.notes !== (tournament.notes || '')
      )
    : Object.values(formData).some((v) => v.trim() !== '');

  return (
    <Modal
      isOpen
      onClose={onClose}
      isDirty={isDirty}
      labelledById="tournament-modal-title"
      zIndex={1002}
      contentStyle={{
        width: isMobile ? 'calc(100vw - 32px)' : '100%',
        maxWidth: isMobile ? 'calc(100vw - 32px)' : '600px',
        maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      }}
    >
        {/* Header */}
        <div style={styles.header}>
          <h2 id="tournament-modal-title" style={styles.title}>
            {tournament ? 'Edit Tournament' : 'Add Tournament'}
          </h2>
          <button onClick={onClose} aria-label="Close" style={styles.closeButton}>
            <X size={24} aria-hidden="true" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formContent}>
            {/* Nine labels in here pointed at nothing — no htmlFor, no id — so
                clicking one focused nothing and a screen reader read the boxes
                unnamed. The ids fix that; the primitives bring the shared
                focus, invalid and disabled treatment with them. */}
            {/* Tournament Name */}
            <div style={styles.formGroup}>
              <label htmlFor="tournament-name" style={styles.label}>
                Tournament Name <span style={styles.required}>*</span>
              </label>
              <TextInput
                id="tournament-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                invalid={!!errors.name}
                aria-describedby={errors.name ? 'tournament-name-error' : undefined}
                style={{
                  ...styles.input,
                  ...(errors.name ? styles.inputError : {}),
                }}
                placeholder="e.g., Spring Showcase"
              />
              {errors.name && <span id="tournament-name-error" role="alert" style={styles.errorText}>{errors.name}</span>}
            </div>

            {/* Facility Name */}
            <div style={styles.formGroup}>
              <label htmlFor="tournament-facility" style={styles.label}>Facility Name</label>
              <TextInput
                id="tournament-facility"
                type="text"
                name="facility_name"
                value={formData.facility_name}
                onChange={handleChange}
                style={styles.input}
                placeholder="e.g., Perfect Game Complex"
              />
            </div>

            {/* Address 1 */}
            <div style={styles.formGroup}>
              <label htmlFor="tournament-address1" style={styles.label}>Address Line 1</label>
              <TextInput
                id="tournament-address1"
                type="text"
                name="address1"
                value={formData.address1}
                onChange={handleChange}
                style={styles.input}
                placeholder="Street address"
              />
            </div>

            {/* Address 2 */}
            <div style={styles.formGroup}>
              <label htmlFor="tournament-address2" style={styles.label}>Address Line 2</label>
              <TextInput
                id="tournament-address2"
                type="text"
                name="address2"
                value={formData.address2}
                onChange={handleChange}
                style={styles.input}
                placeholder="Apt, suite, building (optional)"
              />
            </div>

            {/* City, State, Zip - Grid */}
            <div style={{...styles.formRow, gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr'}}>
              <div style={styles.formGroup}>
                <label htmlFor="tournament-city" style={styles.label}>
                  City <span style={styles.required}>*</span>
                </label>
                <TextInput
                  id="tournament-city"
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  invalid={!!errors.city}
                  aria-describedby={errors.city ? 'tournament-city-error' : undefined}
                  style={{
                    ...styles.input,
                    ...(errors.city ? styles.inputError : {}),
                  }}
                  placeholder="City"
                />
                {errors.city && <span id="tournament-city-error" role="alert" style={styles.errorText}>{errors.city}</span>}
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="tournament-state" style={styles.label}>
                  State <span style={styles.required}>*</span>
                </label>
                <Select
                  id="tournament-state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  invalid={!!errors.state}
                  aria-describedby={errors.state ? 'tournament-state-error' : undefined}
                  style={{
                    ...styles.select,
                    ...(errors.state ? styles.inputError : {}),
                  }}
                >
                  <option value="">State</option>
                  {US_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </Select>
                {errors.state && <span id="tournament-state-error" role="alert" style={styles.errorText}>{errors.state}</span>}
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="tournament-zip" style={styles.label}>ZIP Code</label>
                <TextInput
                  id="tournament-zip"
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="ZIP"
                  maxLength={10}
                />
              </div>
            </div>

            {/* Start Date, End Date - Grid (equal sized) */}
            <div style={{...styles.formRowEqual, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr'}}>
              <div style={styles.formGroup}>
                <label htmlFor="tournament-start-date" style={styles.label}>
                  Start Date <span style={styles.required}>*</span>
                </label>
                <TextInput
                  id="tournament-start-date"
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  invalid={!!errors.start_date}
                  aria-describedby={errors.start_date ? 'tournament-start-date-error' : undefined}
                  style={{
                    ...styles.input,
                    ...(errors.start_date ? styles.inputError : {}),
                  }}
                />
                {errors.start_date && (
                  <span id="tournament-start-date-error" role="alert" style={styles.errorText}>{errors.start_date}</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="tournament-end-date" style={styles.label}>
                  End Date <span style={styles.required}>*</span>
                </label>
                <TextInput
                  id="tournament-end-date"
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  invalid={!!errors.end_date}
                  aria-describedby={errors.end_date ? 'tournament-end-date-error' : undefined}
                  style={{
                    ...styles.input,
                    ...(errors.end_date ? styles.inputError : {}),
                  }}
                />
                {errors.end_date && <span id="tournament-end-date-error" role="alert" style={styles.errorText}>{errors.end_date}</span>}
              </div>
            </div>

            {/* Notes */}
            <div style={styles.formGroup}>
              <label htmlFor="tournament-notes" style={styles.label}>Notes</label>
              <TextArea
                id="tournament-notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                style={styles.textarea}
                placeholder="Add any additional information..."
                rows={4}
              />
            </div>
          </div>

          {/* Footer */}
          <div style={styles.footer}>
            <button type="button" onClick={onClose} style={styles.cancelButton}>
              Cancel
            </button>
            <button type="submit" disabled={submitting} style={{ ...styles.submitButton, ...(submitting ? { background: '#9CA3AF', cursor: 'not-allowed' } : {}) }}>
              {submitting ? 'Saving...' : tournament ? 'Save Changes' : 'Create Tournament'}
            </button>
          </div>
        </form>
    </Modal>
  );
};

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

const getStyles = (isMobile: boolean): { [key: string]: React.CSSProperties } => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1002,
    padding: isMobile ? '16px' : '20px',
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    width: isMobile ? 'calc(100vw - 32px)' : '100%',
    maxWidth: isMobile ? 'calc(100vw - 32px)' : '600px',
    maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: isMobile ? '16px' : '24px',
    borderBottom: '1px solid #E5E7EB',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1F2937',
    margin: 0,
    fontFamily: 'Manrope, sans-serif',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    color: '#6B7280',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    transition: 'all 0.2s',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  formContent: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
  },
  formGroup: {
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'column',
  },
  formRow: {
    display: 'grid',
    gap: '16px',
    marginBottom: '20px',
  },
  formRowEqual: {
    display: 'grid',
    gap: '16px',
    marginBottom: '20px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: '8px',
    fontFamily: 'Manrope, sans-serif',
  },
  required: {
    color: '#EF4444',
  },
  input: {
    width: '100%',
    maxWidth: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontFamily: 'Manrope, sans-serif',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box' as const,
    WebkitAppearance: 'none' as const,
    MozAppearance: 'none' as const,
    appearance: 'none' as const,
  },
  /* Right padding clears the chevron <Select> draws for itself. */
  select: {
    padding: '10px 34px 10px 12px',
    fontSize: '14px',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontFamily: 'Manrope, sans-serif',
    transition: 'border-color 0.2s',
  },
  textarea: {
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontFamily: 'Manrope, sans-serif',
    resize: 'vertical',
    transition: 'border-color 0.2s',
  },
  inputError: {
    borderColor: '#EF4444',
  },
  errorText: {
    fontSize: '12px',
    color: '#EF4444',
    marginTop: '4px',
    fontFamily: 'Manrope, sans-serif',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid #E5E7EB',
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    color: '#6B7280',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'Manrope, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  submitButton: {
    padding: '10px 20px',
    backgroundColor: '#F9550D',
    border: 'none',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'Manrope, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
});

export default AddEditTournamentModal;
